import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Resources from "TestManagement/Scripts/Resources/TFS.Resources.TestManagement";
import * as Utils_String from "VSS/Utils/String";
import { Colors } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Colors";
import { DirectoryPivotType, ITestPlan, IUserOptions } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Contracts";
import { Fabric } from "OfficeFabric/Fabric";
import {
    IFavoriteItemPicker,
    FavoriteItemPicker,
    IFavoriteItem,
    IFavoriteItemPickerProps,
    IFavoritePickListItem
} from "Favorites/Controls/FavoriteItemPicker";
import { FavoriteTypes } from "TfsCommon/Scripts/Favorites/Constants";
import { getDefaultWebContext } from "VSS/Context";
import { getLocalService } from "VSS/Service";
import { getService, VssService } from "VSS/Service";
import { HubHeader } from "VSSUI/Components/HubHeader/HubHeader";
import { HubsService } from "VSS/Navigation/HubsService";
import { IHubBreadcrumbItem } from "VSSUI/Components/HubHeader/HubBreadcrumb.Props";
import { IVssIconProps, VssIcon, VssIconType } from "VSSUI/VssIcon";
import { LicenseAndFeatureFlagUtils } from "TestManagement/Scripts/Utils/TFS.TestManagement.LicenseAndFeatureFlagUtils";
import * as Service from "VSS/Service";
import { TelemetryService } from "TestManagement/Scripts/TFS.TestManagement.Telemetry";
import { TestPlan } from "TFS/TestManagement/Contracts";
import { TestPlanActionsCreator } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Actions/TestPlanActionsCreator";
import { TestPlansHubSettingsService } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/SettingsService";
import { TestPlanStore } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Stores/TestPlanStore";
import { UrlHelper } from "TestManagement/Scripts/TFS.TestManagement.Utils";
import { TestPlanResultsSource, ITestResultsSummary } from "TestManagement/Scripts/Scenarios/NewTestPlanHub/Sources/TestPlanResultsSource";


export interface IProps {
    containerStyle: React.CSSProperties;
    store: TestPlanStore;
    actionsCreator: TestPlanActionsCreator;
    getFavoriteItemPicker: (props: IFavoriteItemPickerProps) => IFavoriteItemPicker;
}

export class Breadcrumb extends React.PureComponent<IProps, TestPlan> {
    constructor(props: IProps) {
        super(props);

        this._store = this.props.store;
        this._actionsCreator = this.props.actionsCreator;
        this.state = this._store.getData();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element {
        const divStyle = this.props.containerStyle;
        return (
            <div style={divStyle}>
                <HubHeader
                    breadcrumbItems={this._getBreadcrumb()}
                    title={`${this.state.name}`}
                    iconProps={this._getTestPlanIcon()}
                    headerItemPicker={this._getFavoriteItemPicker()}
                    maxBreadcrumbItemWidth="600px"
                />
            </div>
        );
    }

    private _onStoreChanged = () => {
        this.setState(this._store.getData());
    }

    private _getBreadcrumb = (): IHubBreadcrumbItem[] => {
        return [{
            key: "test-plan-breadcrumb",
            text: Resources.TestPlansText,
            onClick: this._onBreadcrumbRootClick,
            ariaLabel: Resources.NavigateToTestPlanDirectory
        }];
    }

    private _onBreadcrumbRootClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        TelemetryService.publishEvent(TelemetryService.featureNewTestPlanHub, TelemetryService.breadcrumbRootClick, Utils_String.empty);

        const url = getService(TestPlansHubSettingsService).userOptions.selectedPivot === DirectoryPivotType.all ?
            UrlHelper.getAllPageUrl() : UrlHelper.getMinePageUrl();
        const hubsService = getLocalService(HubsService);
        hubsService.navigateToHub("ms.vss-test-web.test-newtestplan-hub", url);
    }

    private _getFavoriteItemPicker: () => IFavoriteItemPicker = () => {
        const webContext = getDefaultWebContext();

        const props: IFavoriteItemPickerProps = {
            favoritesContext: {
                artifactTypes: [FavoriteTypes.TEST_PLAN],
                artifactScope: {
                    id: webContext.project.id,
                    name: webContext.project.name,
                    type: "Project"
                }
            },
            selectedItem: {
                id: this.state.id.toString(),
                name: this.state.name
            },
            getFavoriteItemIcon: (item: IFavoriteItem) => this._getTestPlanIcon(),
            getFavoriteItemHref: this._getFavoriteItemHref,
            onFavoriteClick: this._onFavoriteClick,
            onFavorited: this._onFavorited,
            onUnFavorited: this._onUnFavorited,
            getAriaLabelForItem: (item) => { return Resources.TestPlanText + " " + item.artifactName + " " + Resources.TestPlanRemoveFromFavoritesButton; }
        };

        return this.props.getFavoriteItemPicker(props);
    }

    private _getTestPlanIcon = (): IVssIconProps => {
        const iconStyles = { root: { color: Colors.darkTeal } };
        return { iconName: "test-plan", iconType: VssIconType.bowtie, styles: iconStyles };
    }

    private _onFavoriteClick = (item: IFavoriteItem) => {
        const planId = parseInt(item.id);
        this._actionsCreator.navigateToPlan({ id: planId, name: item.name } as TestPlan);
    }

    private _getFavoriteItemHref = (item: IFavoriteItem): string => {
        const planId = parseInt(item.id);
        return UrlHelper.getPlanUrl(planId);
    }

    private _onFavorited = (item: IFavoriteItem) => {
        TelemetryService.publishEvent(TelemetryService.featureNewTestPlanHub, TelemetryService.unfavoritedThroughBreadcrumb, Utils_String.empty);
    }

    private _onUnFavorited = (item: IFavoriteItem) => {
        TelemetryService.publishEvent(TelemetryService.featureNewTestPlanHub, TelemetryService.unfavoritedThroughBreadcrumb, Utils_String.empty);
    }
    private _store: TestPlanStore;
    private _actionsCreator: TestPlanActionsCreator;
}

export function mountBreadcrumb(element: HTMLElement, containerStyle: React.CSSProperties): void {

    ReactDOM.render(
        <Fabric>
            <div style={{ display: "inline-block" }}>
                <Breadcrumb containerStyle={containerStyle}
                    store={TestPlanStore.getInstance()}
                    actionsCreator={TestPlanActionsCreator.getInstance()}
                    getFavoriteItemPicker={(props: IFavoriteItemPickerProps) => new FavoriteItemPicker(props)} />
            </div>
            <div id="progress-report-entry-point" style={{ display: "none" }}>
                <span id="results-insight"> </span>
                <a id="progress-report-link" style={{ color: "#0078d4", cursor: "pointer" }}> { Resources.ProgressReportView }</a>
            </div>
        </Fabric>, element);

    if (LicenseAndFeatureFlagUtils.isAnalyticsHubForManualTestEnabled()) {

        $('#progress-report-link').on('click', () => {
            TelemetryService.publishEvents(
                TelemetryService.NavigateToProgressReportFromTesthubLite,
                {
                    planId: TestPlanStore.getInstance().getData().id,
                    planName: TestPlanStore.getInstance().getData().name
                }
            );
            const url = "_testManagement/analytics/progressreport";
            const hubsService = Service.getLocalService(HubsService);
            hubsService.navigateToHub("ms.vss-web.route.manualtest-analytics-hub-route", url);
        });

        updateResultsSummary();
    }
}

function updateResultsSummary() {
    
    new TestPlanResultsSource().getTestResultsSummary(TestPlanStore.getInstance().getData().id).then((reportSummary: ITestResultsSummary) => {
        if (reportSummary.TotalCount) {
            if (reportSummary.ExecPct === 0) {
                $("#results-insight")[0].innerHTML = Utils_String.format(Resources.ProgressReportRun, reportSummary.ExecPct);
            }
            else {
                $("#results-insight")[0].innerHTML = Utils_String.format(Resources.ProgressReportRunPassed, reportSummary.ExecPct, reportSummary.PassedPct);
            }
            $("#progress-report-entry-point")[0].style.display = "inline-block";
        }
        else {
            $("#progress-report-entry-point")[0].style.display = "none";
        }

        if (!LicenseAndFeatureFlagUtils.isDisableAutoRefreshOfEntryPointTextForAnalyticsHubForManualTestEnabled()) {
            // Update results summary after 5 mins.
            setTimeout(updateResultsSummary, 1000 * 60 * 5);
        }
    }, (e: Error) => {
        TelemetryService.publishEvents(TelemetryService.NavigateToProgressReportFromTesthubLiteError, {
            message: e.message,
            stack: e.stack,
            error: e
        });
    });
}
